import React, { useState, useEffect } from 'react';
import { llmConfigService } from '../services/api';

const LlmModelsTab = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({
    modelName: '',
    displayName: '',
    provider: 'vertex',
    defaultTemperature: 1.0,
    costInputTokens: 0,
    costOutputTokens: 0,
    maxInputTokens: 1000000,
    maxOutputTokens: 8192,
    isActive: true,
    cacheEnabled: false
  });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await llmConfigService.getAvailableModels(true);
      setModels(response.data || []);
    } catch (error) {
      console.error('Error loading models:', error);
      setMessage({ type: 'error', text: 'Failed to load models' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (model = null) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        modelName: model.modelName,
        displayName: model.displayName,
        provider: model.provider,
        defaultTemperature: parseFloat(model.defaultTemperature),
        costInputTokens: parseFloat(model.costInputTokens),
        costOutputTokens: parseFloat(model.costOutputTokens),
        maxInputTokens: model.maxInputTokens,
        maxOutputTokens: model.maxOutputTokens,
        isActive: model.isActive,
        cacheEnabled: model.cacheEnabled
      });
    } else {
      setEditingModel(null);
      setFormData({
        modelName: '',
        displayName: '',
        provider: 'vertex',
        defaultTemperature: 1.0,
        costInputTokens: 0,
        costOutputTokens: 0,
        maxInputTokens: 1000000,
        maxOutputTokens: 8192,
        isActive: true,
        cacheEnabled: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModel(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingModel) {
        await llmConfigService.updateModel(editingModel.id, formData);
        setMessage({ type: 'success', text: 'Model updated successfully' });
      } else {
        await llmConfigService.createModel(formData);
        setMessage({ type: 'success', text: 'Model created successfully' });
      }
      
      await loadModels();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving model:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save model';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleDelete = async (modelId, modelName) => {
    if (!window.confirm(`Are you sure you want to delete the model "${modelName}"?`)) {
      return;
    }

    try {
      await llmConfigService.deleteModel(modelId);
      setMessage({ type: 'success', text: 'Model deleted successfully' });
      await loadModels();
    } catch (error) {
      console.error('Error deleting model:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete model';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  // Filter models based on search query
  const filteredModels = models.filter(model => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      model.modelName?.toLowerCase().includes(query) ||
      model.displayName?.toLowerCase().includes(query) ||
      model.provider?.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">LLM Models</h3>
          <p className="text-sm text-gray-600">
            Manage available language models and their configurations.
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Model
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Search Filter */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by model name, display name, or provider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="max-h-[calc(100vh-450px)] overflow-y-auto pr-2">
          {filteredModels.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {filteredModels.map((model) => (
                <ModelCard
                  key={model.id}
                  model={model}
                  onEdit={() => handleOpenModal(model)}
                  onDelete={() => handleDelete(model.id, model.displayName)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <p>{searchQuery ? 'No models match your search' : 'No models available'}</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <ModelFormModal
          model={editingModel}
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

const ModelCard = ({ model, onEdit, onDelete }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">{model.displayName}</h4>
            {model.isActive ? (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Active
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                Inactive
              </span>
            )}
            {model.cacheEnabled && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                Cache Enabled
              </span>
            )}
          </div>
          
          <p className="text-sm text-gray-600 mb-3 font-mono">{model.modelName}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600">
            <div>
              <span className="font-medium">Provider:</span> {model.provider}
            </div>
            <div>
              <span className="font-medium">Temperature:</span> {parseFloat(model.defaultTemperature)}
            </div>
            <div>
              <span className="font-medium">Input Cost:</span> ${parseFloat(model.costInputTokens).toFixed(4)}/1K
            </div>
            <div>
              <span className="font-medium">Output Cost:</span> ${parseFloat(model.costOutputTokens).toFixed(4)}/1K
            </div>
            <div>
              <span className="font-medium">Max Input:</span> {model.maxInputTokens.toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Max Output:</span> {model.maxOutputTokens.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onEdit}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Edit model"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Delete model"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ModelFormModal = ({ model, formData, onInputChange, onSubmit, onClose }) => {
  const isEditing = !!model;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Model' : 'Add New Model'}
          </h3>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Model Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model Name *
              </label>
              <input
                type="text"
                value={formData.modelName}
                onChange={(e) => onInputChange('modelName', e.target.value)}
                disabled={isEditing}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., gemini-2.5-pro"
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Model name cannot be changed</p>
              )}
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => onInputChange('displayName', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Gemini 2.5 Pro"
              />
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider *
              </label>
              <select
                value={formData.provider}
                onChange={(e) => onInputChange('provider', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="vertex">Vertex AI</option>
                <option value="vertex-maas">Vertex AI MaaS</option>
              </select>
            </div>

            {/* Default Temperature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Temperature *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="2"
                value={formData.defaultTemperature}
                onChange={(e) => onInputChange('defaultTemperature', parseFloat(e.target.value))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Cost Input Tokens */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost Input Tokens (per 1K) *
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.costInputTokens}
                onChange={(e) => onInputChange('costInputTokens', parseFloat(e.target.value))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Cost Output Tokens */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost Output Tokens (per 1K) *
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.costOutputTokens}
                onChange={(e) => onInputChange('costOutputTokens', parseFloat(e.target.value))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Max Input Tokens */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Input Tokens *
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxInputTokens}
                onChange={(e) => onInputChange('maxInputTokens', parseInt(e.target.value))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Max Output Tokens */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Output Tokens *
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxOutputTokens}
                onChange={(e) => onInputChange('maxOutputTokens', parseInt(e.target.value))}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => onInputChange('isActive', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.cacheEnabled}
                onChange={(e) => onInputChange('cacheEnabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Cache Enabled</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {isEditing ? 'Update Model' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LlmModelsTab;
